// Historico da propria equipe. Atualiza por SSE (o layout invalida a consulta
// a cada evento `submission`).

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { SubmissionDetailDto, SubmissionListItemDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { ErrorBox, InfoBox, Spinner, VerdictBadge } from "../../components/ui.js";
import {
  VERDICT_LABEL,
  formatContestTime,
  formatMemory,
  formatTime,
} from "../../lib/format.js";
import { LANGUAGE_LABEL } from "../../lib/languages.js";

export function TeamSubmissionsPage(): React.JSX.Element {
  const [aberta, setAberta] = useState<string | null>(null);

  const submissions = useQuery({
    queryKey: ["submissions"],
    queryFn: () => api.get<SubmissionListItemDto[]>("/submissions"),
  });

  if (submissions.isPending) return <Spinner />;
  if (submissions.error) {
    return <ErrorBox message={(submissions.error as Error).message} />;
  }

  if (submissions.data.length === 0) {
    return (
      <p className="rounded border border-slate-200 bg-white p-8 text-center text-slate-500">
        Voce ainda nao enviou nenhuma solucao.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">Hora</th>
            <th className="px-4 py-2 text-left">Problema</th>
            <th className="px-4 py-2 text-left">Linguagem</th>
            <th className="px-4 py-2 text-left">Veredito</th>
            <th className="px-4 py-2 text-right">Tempo</th>
            <th className="px-4 py-2 text-right">Memoria</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {submissions.data.map((s) => (
            <>
              <tr key={s.id} className="border-b border-slate-100">
                <td className="px-4 py-2 font-mono tabular-nums text-slate-600">
                  {formatContestTime(s.contestSeconds)}
                </td>
                <td className="px-4 py-2 font-semibold">{s.problemLabel}</td>
                <td className="px-4 py-2 text-slate-600">
                  {LANGUAGE_LABEL[s.language]}
                </td>
                <td className="px-4 py-2">
                  <VerdictBadge status={s.status} verdict={s.verdict} />
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                  {formatTime(s.timeMs)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                  {formatMemory(s.memoryKb)}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setAberta(aberta === s.id ? null : s.id)}
                    className="text-xs text-sky-700 hover:underline"
                  >
                    {aberta === s.id ? "fechar" : "detalhe"}
                  </button>
                </td>
              </tr>
              {aberta === s.id && (
                <tr key={`${s.id}-detalhe`}>
                  <td colSpan={7} className="bg-slate-50 px-4 py-4">
                    <Detalhe id={s.id} />
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Detalhe({ id }: { id: string }) {
  const detalhe = useQuery({
    queryKey: ["submission", id],
    queryFn: () => api.get<SubmissionDetailDto>(`/submissions/${id}`),
  });

  if (detalhe.isPending) return <Spinner label="Carregando..." />;
  if (detalhe.error) return <ErrorBox message={(detalhe.error as Error).message} />;

  const d = detalhe.data;

  return (
    <div className="space-y-3">
      {d.verdict && d.verdict !== "AC" && (
        <InfoBox>
          <strong>{VERDICT_LABEL[d.verdict]}.</strong>{" "}
          {d.verdict === "CE"
            ? "O codigo nao compilou; a saida do compilador esta abaixo."
            : "O juiz nao informa qual caso de teste falhou."}
        </InfoBox>
      )}

      {d.compileOutput && (
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Saida do compilador
          </h3>
          <pre className="max-h-64 overflow-auto rounded bg-slate-900 p-3 text-xs text-rose-200">
            <code>{d.compileOutput}</code>
          </pre>
        </div>
      )}

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Codigo enviado
        </h3>
        <pre className="max-h-96 overflow-auto rounded bg-slate-900 p-3 text-xs leading-relaxed text-slate-100">
          <code>{d.sourceCode}</code>
        </pre>
      </div>
    </div>
  );
}
